import classNames from 'classnames';
import { Children, ComponentProps, ReactNode, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import useIsOverflowing from '@sorare/core/src/hooks/ui/useIsOverflowing';
import useScrollPosition from '@sorare/core/src/hooks/ui/useScrollPosition';
import { range } from '@sorare/core/src/lib/arrays';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { hideScrollbar } from '@sorare/core/src/style/utils';

const activeBackground = css`
  background-color: rgba(var(--c-rgb-green-300), 0.1);
`;
const borderStyle = css`
  border: 0 solid var(--c-neutral-300);
`;

const Td = styled.td`
  padding: 1px 0;
  & > div {
    padding-inline-start: var(--triple-unit);
    text-align: center;
  }
  &:first-child > div {
    padding-inline-start: var(--double-unit);
  }
  &:last-of-type > div {
    padding-inline-end: var(--double-unit);
  }
  &.bold {
    font-weight: var(--t-bold);
  }
  &.secondary {
    color: var(--c-neutral-600);
  }
  &.tertiary {
    color: var(--c-neutral-400);
  }
  &.active {
    color: var(--c-green-800);
  }
  @media ${tabletAndAbove} {
    padding-top: var(--half-unit);
    padding-bottom: var(--half-unit);
  }
`;

type TooltipProps = Omit<ComponentProps<typeof FormattedMessage>, 'children'>;

export type CellProps = {
  children: ReactNode;
  className?: string;
  title?: string | TooltipProps;
  colSpan?: number;
  bold?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  active?: boolean;
};

const CellContent = ({ children, title }: CellProps) => {
  if (typeof title === 'string') {
    return (
      <div>
        <Tooltip title={title}>{children as any}</Tooltip>
      </div>
    );
  }

  return !title?.id || !title?.defaultMessage ? (
    <div>{children}</div>
  ) : (
    <div>
      <Tooltip title={<FormattedMessage {...title} />}>
        {children as any}
      </Tooltip>
    </div>
  );
};

export const StatsTableCell = ({
  children,
  className,
  title = {},
  colSpan,
  bold = false,
  secondary = false,
  tertiary = false,
  active = false,
}: CellProps) => {
  return (
    <Td
      colSpan={colSpan}
      className={classNames(className, { bold, secondary, tertiary, active })}
    >
      <CellContent title={title}>{children}</CellContent>
    </Td>
  );
};

export const StatsTableEmptyCell = () => (
  <StatsTableCell tertiary>–</StatsTableCell>
);

const Tr = styled.tr`
  &.section.sectionFirst {
    & ${Td} {
      padding-top: var(--unit);
      @media ${tabletAndAbove} {
        padding-top: var(--intermediate-unit);
      }
    }
  }
  &.section.sectionLast {
    & ${Td} {
      padding-bottom: var(--unit);
      @media ${tabletAndAbove} {
        padding-bottom: var(--intermediate-unit);
      }
    }
  }
  &:not(.section),
  &.section.sectionLast {
    ${borderStyle}
    border-bottom-width: 1px;
  }
  &.active ${Td} > div {
    ${activeBackground}
  }
`;

type RowProps = {
  children: ReactNode;
  section?: boolean;
  sectionFirst?: boolean;
  sectionLast?: boolean;
  active?: boolean;
  className?: string;
};

export const StatsTableRow = ({
  children,
  section,
  sectionFirst,
  sectionLast,
  active,
  className,
}: RowProps) => {
  return (
    <Tr
      className={classNames(className, {
        section,
        sectionFirst,
        sectionLast,
        active,
      })}
    >
      {children}
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <th> </th>
    </Tr>
  );
};

const THead = styled.thead`
  & ${Tr} {
    ${borderStyle}
    border-width: 1px 0;
  }
  & ${Td} {
    padding-top: var(--half-unit);
    padding-bottom: var(--half-unit);
  }
  &.section ${Td} {
    @media ${tabletAndAbove} {
      padding-top: var(--intermediate-unit);
      padding-bottom: var(--intermediate-unit);
    }
  }
`;

const ColGroup = styled.colgroup`
  &.fixed-width > * {
    width: 25%;
    &:first-child {
      width: var(--unit);
    }
  }
`;

type HeaderProps = { children: ReactNode; section?: boolean };

export const StatsTableHeader = ({ children, section }: HeaderProps) => {
  const nbChildren = Children.toArray(children).length;
  return (
    <>
      <ColGroup className={classNames({ 'fixed-width': nbChildren < 6 })}>
        {range(nbChildren).map(i => (
          <col key={`col_${i}`} />
        ))}
      </ColGroup>
      <THead className={classNames({ section })}>
        <StatsTableRow>{children}</StatsTableRow>
      </THead>
    </>
  );
};

const Wrapper = styled.div`
  ${hideScrollbar}
  --shadow-size: var(--unit);
  --shadow-color: rgba(var(--c-static-rgb-neutral-1000), 0.2);
  position: relative;
  isolation: isolate;
  overflow: auto;
  tbody ${Td}:first-child {
    letter-spacing: -0.04em;
    white-space: nowrap;
  }
  &.alignFirstColumnStart ${Td}:first-child > div {
    text-align: start;
  }
  &.pinFirstColumn ${Td}:first-child {
    background-color: var(--current-background-color, var(--c-neutral-100));
    position: sticky;
    inset-inline-start: 0;
    padding-inline-end: var(--unit);
    z-index: 100;
  }
  &.firstColumnShadow ${Td}:first-child:after {
    content: '';
    box-shadow: var(--shadow-size) 0 var(--shadow-size)
      calc(-1 * var(--shadow-size)) var(--shadow-color) inset;
    position: absolute;
    inset-block-start: 0;
    inset-inline-end: calc(-1 * var(--shadow-size));
    height: 100%;
    width: var(--shadow-size);
  }
  *[dir='rtl'] &.firstColumnShadow ${Td}:first-child:after {
    box-shadow: calc(-1 * var(--shadow-size)) 0 var(--shadow-size)
      calc(-1 * var(--shadow-size)) var(--shadow-color) inset;
  }
  @media ${laptopAndAbove} {
    &.rightShadow {
      box-shadow: calc(-1 * var(--shadow-size)) 0 var(--shadow-size)
        calc(-1 * var(--shadow-size)) var(--shadow-color) inset;
    }
    *[dir='rtl'] &.rightShadow {
      box-shadow: var(--shadow-size) 0 var(--shadow-size)
        calc(-1 * var(--shadow-size)) var(--shadow-color) inset;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  color: var(--c-neutral-1000);
  border-collapse: collapse;
  font: var(--t-14);
  @media ${tabletAndAbove} {
    font: var(--t-16);
  }
`;

type Props = {
  children: ReactNode;
  className?: string;
  alignFirstColumnStart?: boolean;
  pinFirstColumn?: boolean;
};

export const StatsTable = ({
  children,
  className,
  alignFirstColumnStart,
  pinFirstColumn,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(containerRef);
  const { scrollPosition } = useScrollPosition(containerRef);
  const showShadow = pinFirstColumn && isOverflowing;

  return (
    <Wrapper
      ref={containerRef}
      className={classNames(className, {
        alignFirstColumnStart,
        pinFirstColumn,
        firstColumnShadow: showShadow && scrollPosition !== 'start',
        rightShadow: showShadow && scrollPosition !== 'end',
      })}
    >
      <Table>{children}</Table>
    </Wrapper>
  );
};
